import { createSelector } from 'reselect';
import { compact } from 'lodash';
import { PayInMethodCode } from 'constants/pay-in-method';
import { PhoneRequirement } from 'constants/shipping-contact';
import { UiState } from 'constants/ui';
import * as shippingContactSelectors from 'state/shipping-contact/selectors';
import { stateName, DeliveryType, FieldName } from './constants';
import { selectors as shippingSelectors } from './shipping';

const checkoutState = state => state[stateName];

const localState = state => checkoutState(state).checkout;

export const getCheckoutData = state => localState(state).checkoutData;
export const getTransactionId = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.id) || null);
export const getTransactionChecksum = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.checksum) || '');
export const getSelectedPayInMethod = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.buyer.payInMethod) || null);
export const getPayInMethodsConfig = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.buyer.payInMethodsConfig) || null);
export const getPricing = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.pricing) || null);
export const getCurrencyConversion = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.pricing.conversion) || null);
export const getRecommendedPayInMethod = createSelector(getPayInMethodsConfig, payInMethodsConfig => payInMethodsConfig !== null && payInMethodsConfig !== void 0 && payInMethodsConfig.recommended ? payInMethodsConfig.recommended[0] : null);
export const getPayInMethodIncentive = createSelector(getPayInMethodsConfig, payInMethodsConfig => (payInMethodsConfig === null || payInMethodsConfig === void 0 ? void 0 : payInMethodsConfig.incentive) || null);
export const getPayInMethodPromotion = createSelector(getPayInMethodsConfig, payInMethodsConfig => (payInMethodsConfig === null || payInMethodsConfig === void 0 ? void 0 : payInMethodsConfig.promotion) || null);
export const getIsAddCardRecommendationEnabled = createSelector(getPayInMethodIncentive, getRecommendedPayInMethod, (payInMethodIncentive, recommendedPayInMethod) => {
  if (payInMethodIncentive) return false;
  if (!recommendedPayInMethod) return false;
  const {
    payInMethod,
    externalCard
  } = recommendedPayInMethod;
  return payInMethod.code === PayInMethodCode.CreditCard && !externalCard;
});
export const getIsUseExistingCardRecommendationEnabled = createSelector(getPayInMethodIncentive, getRecommendedPayInMethod, (payInMethodIncentive, recommendedPayInMethod) => {
  if (payInMethodIncentive) return false;
  if (!recommendedPayInMethod) return false;
  const {
    payInMethod,
    externalCard
  } = recommendedPayInMethod;
  return payInMethod.code === PayInMethodCode.CreditCard && !!externalCard;
});
export const getSelectedCardId = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.buyer.creditCardId) || null);
export const getSelectedCard = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.buyer.card) || null);
export const getShipmentPrice = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.pricing.shipping.finalPrice) || null);
export const getOfflineVerificationService = createSelector(getCheckoutData, checkoutData => checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.services.offlineVerification);
export const getIsPaymentProcessing = state => localState(state).isPaymentProcessing;
export const getTwoFactorVerification = state => localState(state).twoFactorVerification;
export const getFormState = state => localState(state).formState;
export const getPayment = state => localState(state).payment;
export const getPaymentError = state => localState(state).formState.errors.paymentError;
export const getUiState = state => localState(state).ui;
export const getBlikPaymentAuth = state => localState(state).blikPaymentAuth;
export const getPaymentAuthAction = state => localState(state).paymentAuthAction;
export const getIsPaymentAvailable = createSelector(getCheckoutData, checkoutData => !!(checkoutData !== null && checkoutData !== void 0 && checkoutData.paymentAvailable));
export const getOrderPrice = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.pricing.base.finalPrice) || null);
export const getOrderItems = createSelector(getCheckoutData, checkoutData => (checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.items) || []);
export const getIsBundleOrder = createSelector(getCheckoutData, checkoutData => {
  if (checkoutData !== null && checkoutData !== void 0 && checkoutData.items) return checkoutData.items.length > 1;
  return false;
});
export const getConfiguration = createSelector(localState, _ref => {
  let {
    configuration
  } = _ref;
  return configuration;
});
export const getThreeDS2Config = createSelector(getConfiguration, config => (config === null || config === void 0 ? void 0 : config.threeDS2) || null);
export const getFieldError = (state, fieldName) => localState(state).ui.errors.byName[fieldName];
export const getFieldErrors = createSelector(getUiState, _ref2 => {
  let {
    errors
  } = _ref2;
  return errors.names;
});
export const getIsSellerBusinessUser = createSelector(getCheckoutData, checkoutData => !!(checkoutData !== null && checkoutData !== void 0 && checkoutData.seller.isBusiness));
export const getAddress = createSelector(getCheckoutData, checkoutData => checkoutData === null || checkoutData === void 0 ? void 0 : checkoutData.buyer.shipmentToAddress);
export const getIsKycConfirmationNeeded = createSelector(getFormState, _ref3 => {
  let {
    errors
  } = _ref3;
  return errors.isKycConfirmationNeeded;
});
export const getIsTwoFactorVerificationNeeded = createSelector(getTwoFactorVerification, twoFactorVerification => !!twoFactorVerification);
export const getHasChecksumError = createSelector(getFormState, _ref4 => {
  let {
    errors
  } = _ref4;
  return errors.hasChecksumError;
});
export const getHasShippingDiscountError = createSelector(getFormState, _ref5 => {
  let {
    errors
  } = _ref5;
  return errors.hasShippingDiscountError;
});
export const getIsPaymentProcessingTakesTooLong = createSelector(getFormState, _ref6 => {
  let {
    errors
  } = _ref6;
  return errors.isPaymentProcessingTakesTooLong;
});
export const getIsPaymentFailedAfterRedirect = createSelector(getFormState, _ref7 => {
  let {
    errors
  } = _ref7;
  return errors.isPaymentFailedAfterRedirect;
});
export const getIsAddressCompleted = createSelector(getAddress, getFieldErrors, (address, fieldErrors) => {
  if (fieldErrors.includes(FieldName.Address)) return false;
  return !!address;
});
export const getIsShipmentCompleted = createSelector(shippingSelectors.getShipment, shippingSelectors.getIsShipmentActionable, (shipment, isShipmentActionable) => {
  if (!(shipment !== null && shipment !== void 0 && shipment.carrier)) return false;
  if (!isShipmentActionable) return true;
  const {
    carrier,
    pickupPoint
  } = shipment;
  const {
    deliveryType
  } = carrier;
  if (!pickupPoint && deliveryType === DeliveryType.PickupPoint) return false;
  return true;
});
export const getIsDeliveryTypesInState = createSelector(shippingSelectors.getDeliveryTypes, shippingSelectors.getSelectedDeliveryType, (deliveryTypes, selectedDeliveryType) => !!deliveryTypes && !!selectedDeliveryType);
export const getBlikPaymentCode = createSelector(getBlikPaymentAuth, blikPaymentAuth => (blikPaymentAuth === null || blikPaymentAuth === void 0 ? void 0 : blikPaymentAuth.blikCode) || null);
export const getIsBlikAuthRequired = createSelector(getCheckoutData, getSelectedPayInMethod, (checkoutData, selectedPayInMethod) => {
  if ((selectedPayInMethod === null || selectedPayInMethod === void 0 ? void 0 : selectedPayInMethod.code) !== PayInMethodCode.BlikDirect) return false;
  if (!checkoutData) return false;
  const orderTotal = parseFloat(checkoutData.pricing.payment.payIn.amount);
  return orderTotal > 0;
});
export const getPaymentMethod = createSelector(getSelectedCard, getSelectedPayInMethod, (card, selectedPayInMethod) => {
  if (!selectedPayInMethod) return null;
  const {
    id,
    code,
    note,
    icon,
    translatedName,
    requiresCreditCard,
    methodChangePossible
  } = selectedPayInMethod;
  if (code === PayInMethodCode.ApplePay) return null;
  if (code === PayInMethodCode.GooglePay) return null;
  if (!requiresCreditCard) return {
    id,
    code,
    note,
    isCard: false,
    brand: icon,
    name: translatedName,
    methodChangePossible
  };
  if (!card) return null;
  return {
    id,
    code,
    cardId: card.id,
    isCard: true,
    brand: card.brand,
    lastFour: card.lastFour,
    isExpired: card.expired,
    isSingleUse: card.isSingleUse,
    methodChangePossible
  };
});
export const getFormattedAddress = createSelector(getAddress, address => compact([address === null || address === void 0 ? void 0 : address.line1, address === null || address === void 0 ? void 0 : address.line2, address === null || address === void 0 ? void 0 : address.city, address === null || address === void 0 ? void 0 : address.state, address === null || address === void 0 ? void 0 : address.postalCode]).join(' '));
export const getCheckoutDataUiState = createSelector(getUiState, _ref8 => {
  let {
    checkoutData
  } = _ref8;
  return checkoutData;
});
export const getIsOfflineVerificationCheckDisabled = createSelector(getIsPaymentProcessing, getCheckoutDataUiState, (isPaymentProcessing, transactionUiState) => isPaymentProcessing || transactionUiState === UiState.Pending);
export const getIsShippingContactCompleted = createSelector(shippingSelectors.getBuyerPhoneRequirement, shippingContactSelectors.getBuyerPhoneNumber, (requirement, buyerPhoneNumber) => !(requirement === PhoneRequirement.Mandatory && !buyerPhoneNumber));
export const getIsShippingContactDisabled = createSelector(getIsShipmentCompleted, shippingSelectors.getBuyerPhoneRequirement, shippingContactSelectors.getShippingContactUiState, (isShipmentCompleted, requirement, uiState) => {
  if (!isShipmentCompleted) return true;
  if (!requirement || requirement === PhoneRequirement.Disabled) return true;
  if (uiState !== UiState.Success) return true;
  return false;
});
export const getIsPaymentCompleted = createSelector(getSelectedCard, getSelectedPayInMethod, (card, selectedPayInMethod) => {
  if (card && !card.expired) return true;
  if (!selectedPayInMethod) return false;
  return !selectedPayInMethod.requiresCreditCard;
});
export const getIsFormCompleted = createSelector(getIsPaymentCompleted, getIsAddressCompleted, getIsShipmentCompleted, getIsShippingContactCompleted, getIsDeliveryTypesInState, getIsPaymentAvailable, function () {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return args.every(Boolean);
});
export const getIsCheckoutUiLoaded = createSelector(getConfiguration, getCheckoutDataUiState, shippingSelectors.getDeliveryTypesUiState, shippingContactSelectors.getShippingContactUiState, (configuration, transactionUiState, deliveryTypesUiState, shippingContactUiState) => !!configuration && transactionUiState === UiState.Success && deliveryTypesUiState === UiState.Success && shippingContactUiState === UiState.Success);